/**
 * Print template spec — the DATA form of a printable document (title block,
 * border, logos, viewports onto the project, scale bar, …).
 *
 * Organisations author templates in the Template Editor; the result is a
 * {@link PrintTemplateSpec} stored on the server (validated with
 * `parsePrintTemplateSpec`) and filled per project by a SheetDoc (see
 * `sheet.ts`), which a single layout engine turns into the SVG preview and
 * the PDF.
 *
 * Coordinate system: page space in MILLIMETERS, origin at the top-left corner
 * of the page, x right, y DOWN. An element's box is `(xMm, yMm, wMm, hMm)`
 * before rotation; `rotationDeg` turns it clockwise around the box center.
 * Children of a group are relative to the group's top-left corner. Element
 * order is paint order (later = on top).
 *
 * SCHEMA: page ids, element ids and field keys are persisted in sheets
 * (viewport settings and field values are keyed by them); never rename them
 * after publishing.
 */

import type { PrintColor } from './color';
import type { PaperOrientation, PaperRef } from './paper';
import type { TextSize } from './units';

export const PRINT_TEMPLATE_SCHEMA_VERSION = 1;

export const PRINT_TEMPLATE_LIMITS = {
  maxPages: 50,
  maxElements: 2000,
  maxGroupDepth: 8,
  maxFields: 100,
  maxStyles: 50,
  maxTextLength: 4000,
  maxLabelLength: 200,
  maxPolylinePoints: 2000,
  maxTableColumns: 20,
  maxTableRows: 200,
  maxLegendItems: 50,
  /** Smallest legible print text, em millimeters (≈ 3 pt). */
  minTextEmMm: 1,
  maxTextEmMm: 300,
  maxLineWeightMm: 20,
} as const;

/** Field keys: letters/digits/underscore, starting with a letter. */
export const PRINT_FIELD_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

// ─── Fonts & text styles ────────────────────────────────────────────────

/** Fonts shipped with the app (open licences, embeddable). */
export const BUNDLED_PRINT_FONTS = ['Inter', 'IBM Plex Sans Condensed', 'Source Serif 4', 'JetBrains Mono', 'osifont'] as const;
export type BundledPrintFont = (typeof BUNDLED_PRINT_FONTS)[number];

/** A bundled family, or an organisation font asset (brand kit). */
export type FontRef = { source: 'bundled'; family: BundledPrintFont } | { source: 'org'; assetId: string };

export type FontWeight = 'normal' | 'bold';

export interface TextStyle {
  font: FontRef;
  size: TextSize;
  weight: FontWeight;
  italic: boolean;
  color: PrintColor;
  /** Line pitch as a multiple of the em size. */
  lineHeight: number;
  /** Extra space between letters, paper millimeters. */
  letterSpacingMm: number;
}

/**
 * A style as written on an element: an optional named style (`styleId`, see
 * {@link PrintTemplateSpec.styles}) plus per-element overrides. Resolution
 * order: template defaults → named style → overrides.
 */
export interface TextStyleRef extends Partial<TextStyle> {
  styleId?: string;
}

export interface NamedTextStyle {
  id: string;
  name: string;
  style: Partial<TextStyle>;
}

// ─── Strokes ────────────────────────────────────────────────────────────

export type LineCap = 'butt' | 'round' | 'square';
export type LineJoin = 'miter' | 'round' | 'bevel';

export interface StrokeStyle {
  color: PrintColor;
  /** Pen width on paper, millimeters (ISO pens: 0.13, 0.18, 0.25, 0.35, 0.5, 0.7, 1.0). */
  weightMm: number;
  /** Dash pattern in paper millimeters; empty = solid. */
  dashMm: number[];
  cap: LineCap;
  join: LineJoin;
}

/** ISO 128 pen widths, millimeters. */
export const ISO_PEN_WEIGHTS_MM: readonly number[] = [0.05, 0.07, 0.09, 0.13, 0.18, 0.25, 0.35, 0.5, 0.7, 1, 1.4, 2];

// ─── Viewports ──────────────────────────────────────────────────────────

/**
 * What a viewport shows.
 * - PLAN_VECTOR: the floor plan (walls, openings, objects, dimensions, rooms) as true vectors at scale
 * - DENSITY_RASTER: the X-ray point density map, rendered at the sheet DPI
 * - HEATMAP_RASTER: a heatmap (e.g. elevation / deviation) of the point cloud, at the sheet DPI
 * - ORTHO_RGB: a top-down orthophoto of the point cloud colors, at the sheet DPI
 */
export const VIEWPORT_KINDS = ['PLAN_VECTOR', 'DENSITY_RASTER', 'HEATMAP_RASTER', 'ORTHO_RGB'] as const;
export type ViewportKind = (typeof VIEWPORT_KINDS)[number];

export function isRasterViewportKind(kind: ViewportKind): boolean {
  return kind !== 'PLAN_VECTOR';
}

// ─── Elements ───────────────────────────────────────────────────────────

export type HorizontalAlign = 'left' | 'center' | 'right';
export type VerticalAlign = 'top' | 'middle' | 'bottom';

interface ElementBase {
  /** Stable id (SCHEMA): letters, digits, `_`, `-`. Unique across the whole template. */
  id: string;
  /** Editor display name. */
  name?: string;
  locked?: boolean;
  hidden?: boolean;
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
  /** Clockwise around the box center. */
  rotationDeg: number;
}

/** Text with `{placeholders}` (see placeholders.ts) — also how fields are placed. */
export interface TextElement extends ElementBase {
  kind: 'text';
  content: string;
  style: TextStyleRef;
  align: HorizontalAlign;
  valign: VerticalAlign;
  /** Shrink the font (down to the minimum legible size) instead of overflowing the box. */
  autoShrink: boolean;
  paddingMm: number;
}

export type ImageSource = { kind: 'asset'; assetId: string } | { kind: 'orgLogo' };
export type ImageFit = 'contain' | 'cover' | 'stretch';

export interface ImageElement extends ElementBase {
  kind: 'image';
  source: ImageSource;
  fit: ImageFit;
  /** Where a contained (or cropped) picture sits in its box; center / middle when missing. */
  align?: HorizontalAlign;
  valign?: VerticalAlign;
}

export interface RectElement extends ElementBase {
  kind: 'rect';
  stroke: StrokeStyle | null;
  fill: PrintColor | null;
  cornerRadiusMm: number;
}

export interface EllipseElement extends ElementBase {
  kind: 'ellipse';
  stroke: StrokeStyle | null;
  fill: PrintColor | null;
}

/** Open or closed polyline; points are box-local millimeters (0..wMm, 0..hMm). */
export interface PolylineElement extends ElementBase {
  kind: 'polyline';
  pointsMm: [number, number][];
  closed: boolean;
  stroke: StrokeStyle | null;
  fill: PrintColor | null;
}

/** A window onto the project; the sheet supplies what/where/at which scale (ViewportSettings). */
export interface ViewportElement extends ElementBase {
  kind: 'viewport';
  /** Human label in the sheet editor ("Main plan", "Density map"). */
  label: string;
  defaultKind: ViewportKind;
  allowedKinds: ViewportKind[];
  defaultScaleDen: number;
  /** Border around the viewport; null = none. */
  frame: StrokeStyle | null;
}

export interface ScaleBarElement extends ElementBase {
  kind: 'scaleBar';
  /** The viewport whose scale it shows (null: the page's first view; older documents). */
  viewportId: string | null;
  /** Number of alternating segments. The total length is picked as a round number of meters that fits `wMm`. */
  segments: number;
  stroke: StrokeStyle;
  fill: PrintColor;
  labelStyle: TextStyleRef;
}

export type NorthArrowVariant = 'arrow' | 'triangle' | 'compass';

export interface NorthArrowElement extends ElementBase {
  kind: 'northArrow';
  /** Rotates with this viewport's heading; null = always points up. */
  viewportId: string | null;
  variant: NorthArrowVariant;
  stroke: StrokeStyle;
  fill: PrintColor;
  labelStyle: TextStyleRef;
}

export interface LegendItem {
  label: string;
  color: PrintColor;
}

/**
 * - heatmap: the gradient + range of the linked HEATMAP_RASTER viewport
 * - layers: pen swatches of the linked PLAN_VECTOR viewport's visible layers
 * - manual: the listed items
 */
export type LegendSource = 'heatmap' | 'layers' | 'manual';

export interface LegendElement extends ElementBase {
  kind: 'legend';
  viewportId: string | null;
  source: LegendSource;
  title: string;
  items: LegendItem[];
  style: TextStyleRef;
}

export interface TableColumn {
  id: string;
  header: string;
  widthMm: number;
  /**
   * Cell content template. For revision rows `{rev.rev}`, `{rev.date}`,
   * `{rev.description}`, `{rev.by}` resolve per row; other placeholders work too.
   */
  cell: string;
  align: HorizontalAlign;
}

export type TableRows = { kind: 'revisions'; maxRows: number; newestFirst: boolean } | { kind: 'static'; cells: string[][] };

export interface TableElement extends ElementBase {
  kind: 'table';
  columns: TableColumn[];
  rows: TableRows;
  showHeader: boolean;
  rowHeightMm: number;
  /** Rows stack from the bottom edge up (classic revision block above a title block). */
  growUp: boolean;
  headerStyle: TextStyleRef;
  cellStyle: TextStyleRef;
  stroke: StrokeStyle | null;
}

export interface GroupElement extends ElementBase {
  kind: 'group';
  children: PrintTemplateElement[];
}

export type PrintTemplateElement = TextElement | ImageElement | RectElement | EllipseElement | PolylineElement | ViewportElement | ScaleBarElement | NorthArrowElement | LegendElement | TableElement | GroupElement;

export type PrintTemplateElementKind = PrintTemplateElement['kind'];

// ─── Fields, pages, template ────────────────────────────────────────────

export type PrintFieldType = 'text' | 'multiline' | 'date' | 'select';

/** A value the user fills in per sheet, placed with `{field.<key>}`. */
export interface PrintFieldDef {
  key: string;
  label: string;
  type: PrintFieldType;
  /** Choices for `select`. */
  options: string[];
  /** May contain placeholders (`{project.name}`), resolved when the sheet leaves the field empty. */
  defaultValue: string;
}

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** `perFloor`: the page is emitted once per selected floor, with `{floor.*}` and the viewports bound to that floor. */
export type PageRepeat = 'none' | 'perFloor';

export interface PrintPageSpec {
  id: string;
  name: string;
  paper: PaperRef;
  orientation: PaperOrientation;
  /** Printable-area guide in the editor; elements may still bleed into it. */
  marginsMm: PageMargins;
  repeat: PageRepeat;
  elements: PrintTemplateElement[];
}

export interface PrintTemplateDefaults {
  textStyle: TextStyle;
  stroke: StrokeStyle;
}

export interface PrintTemplateSpec {
  schemaVersion: typeof PRINT_TEMPLATE_SCHEMA_VERSION;
  defaults: PrintTemplateDefaults;
  styles: NamedTextStyle[];
  fields: PrintFieldDef[];
  pages: PrintPageSpec[];
}

// ─── Helpers ────────────────────────────────────────────────────────────

/** Resolves a style reference: template defaults → named style → element overrides. */
export function resolveTextStyle(spec: Pick<PrintTemplateSpec, 'defaults' | 'styles'>, ref: TextStyleRef): TextStyle {
  const named = ref.styleId ? spec.styles.find((s) => s.id === ref.styleId)?.style : undefined;
  const { styleId: _styleId, ...overrides } = ref;
  return { ...spec.defaults.textStyle, ...named, ...stripUndefined(overrides) };
}

function stripUndefined<T extends object>(value: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(value) as (keyof T)[]) {
    if (value[key] !== undefined) out[key] = value[key];
  }
  return out;
}

/** Depth-first walk over every element on a page, including group children. */
export function forEachElement(elements: readonly PrintTemplateElement[], visit: (element: PrintTemplateElement, depth: number) => void, depth = 0): void {
  for (const element of elements) {
    visit(element, depth);
    if (element.kind === 'group') forEachElement(element.children, visit, depth + 1);
  }
}

/** Every viewport element on a page (groups included). */
export function pageViewports(page: PrintPageSpec): ViewportElement[] {
  const out: ViewportElement[] = [];
  forEachElement(page.elements, (element) => {
    if (element.kind === 'viewport') out.push(element);
  });
  return out;
}
